import React, { useEffect, useState, useRef, useCallback } from 'react';

/**
 * GridOverlay component that renders grid lines matching react-grid-layout's calculations
 * Grid lines are centered in the gaps between widgets for visual alignment
 */
function GridOverlay({ 
  cols = 4, 
  rowHeight = 60, 
  margin = [16, 16], 
  containerPadding = [16, 16],
  defaultWidgetHeight = 6,
  gridRef = null,
  layouts = null
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [gridLines, setGridLines] = useState({ verticalLines: [], horizontalLines: [] });

  // Helper function to find the grid container DOM element
  const findGridContainer = useCallback(() => {
    if (!gridRef?.current) {
      return containerRef.current?.parentElement?.querySelector('.react-grid-layout') || null;
    }

    if (gridRef.current.nodeType === 1) {
      return gridRef.current.querySelector?.('.react-grid-layout') || gridRef.current;
    }

    return containerRef.current?.parentElement?.querySelector('.react-grid-layout') || null;
  }, [gridRef]);

  // Track container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      const gridContainer = findGridContainer();
      const targetElement = gridContainer || containerRef.current;
      
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: Math.max(rect.height, 1200)
        });
      }
    };

    // Initial update with delay to ensure grid is rendered
    const timeoutId = setTimeout(updateDimensions, 100);
    updateDimensions();
    
    window.addEventListener('resize', updateDimensions);
    
    // Use ResizeObserver for accurate tracking
    const gridContainer = findGridContainer();
    const targetElement = gridContainer || containerRef.current;
    let resizeObserver = null;
    
    if (targetElement && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(targetElement);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDimensions);
      resizeObserver?.disconnect();
    };
  }, [gridRef, findGridContainer]);

  // Calculate grid lines based on actual widget positions
  useEffect(() => {
    const calculateGridLinesFromDOM = () => {
      if (dimensions.width === 0) {
        return;
      }

      const gridContainer = findGridContainer();
      if (!gridContainer) {
        return;
      }

      const containerRect = gridContainer.getBoundingClientRect();
      const widgetElements = gridContainer.querySelectorAll('.react-grid-item');
      const [marginX, marginY] = margin;
      const [paddingX, paddingY] = containerPadding;
      const availableWidth = dimensions.width - (paddingX * 2);
      const columnWidth = (availableWidth - (marginX * (cols - 1))) / cols;
      
      // Calculate vertical lines (centered in gaps between columns)
      const verticalLines = [];
      for (let i = 0; i < cols - 1; i += 1) {
        const columnEnd = paddingX + ((i + 1) * columnWidth) + (i * marginX);
        verticalLines.push(columnEnd + (marginX / 2));
      }

      // Calculate horizontal lines
      const horizontalLines = [];
      
      if (widgetElements.length === 0) {
        // No widgets yet - use calculation for empty grid
        let y = 0;
        let lineY = paddingY + (y * (rowHeight + marginY)) + (defaultWidgetHeight * rowHeight);
        while (lineY <= dimensions.height) {
          horizontalLines.push(lineY);
          y += defaultWidgetHeight;
          lineY = paddingY + (y * (rowHeight + marginY)) + (defaultWidgetHeight * rowHeight);
        }
      } else {
        // Measure actual widget positions
        const widgetBottoms = new Set();
        const widgetTops = new Set();
        
        Array.from(widgetElements).forEach(el => {
          const rect = el.getBoundingClientRect();
          widgetBottoms.add(Math.round(rect.bottom - containerRect.top));
          widgetTops.add(Math.round(rect.top - containerRect.top));
        });

        const sortedBottoms = Array.from(widgetBottoms).sort((a, b) => a - b);
        const sortedTops = Array.from(widgetTops).sort((a, b) => a - b);
        
        // Place grid lines centered in gaps between widgets
        sortedBottoms.forEach((widgetBottom) => {
          const nextWidgetTop = sortedTops.find(top => top > widgetBottom);
          
          if (nextWidgetTop) {
            // Gap between widgets - center the grid line
            horizontalLines.push(widgetBottom + (nextWidgetTop - widgetBottom) / 2);
          } else {
            // Last widget - place grid line at bottom + half margin for future rows
            horizontalLines.push(widgetBottom + marginY / 2);
          }
        });
        
        // Extend grid lines for empty rows below existing widgets
        const widgetHeights = Array.from(widgetElements).map(el => el.getBoundingClientRect().height);
        const avgWidgetHeight = widgetHeights.reduce((a, b) => a + b, 0) / widgetHeights.length;
        const rowSpacing = avgWidgetHeight + marginY;
        
        let currentY = horizontalLines[horizontalLines.length - 1];
        while (currentY + rowSpacing <= dimensions.height) {
          currentY += rowSpacing;
          horizontalLines.push(currentY);
        }
      }

      setGridLines({ verticalLines, horizontalLines });
    };

    // Delay to ensure widgets are rendered
    const timeoutId = setTimeout(calculateGridLinesFromDOM, 300);
    
    // Recalculate when layouts change (using MutationObserver would be better but interval is simpler)
    const intervalId = setInterval(calculateGridLinesFromDOM, 500);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [dimensions, layouts, cols, rowHeight, margin, containerPadding, defaultWidgetHeight, findGridContainer]);

  const { verticalLines, horizontalLines } = gridLines;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {dimensions.width > 0 && dimensions.height > 0 && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: dimensions.width,
            height: dimensions.height,
            pointerEvents: 'none',
          }}
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        >
          {/* Vertical grid lines */}
          {verticalLines.map((x) => (
            <line
              key={`v-${x}`}
              x1={x}
              y1={containerPadding[1]}
              x2={x}
              y2={dimensions.height}
              stroke="var(--color-border-medium)"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          ))}
          
          {/* Horizontal grid lines - centered in gaps between widgets */}
          {horizontalLines.map((y) => (
            <line
              key={`h-${y}`}
              x1={containerPadding[0]}
              y1={y}
              x2={dimensions.width - containerPadding[0]}
              y2={y}
              stroke="var(--color-border-medium)"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          ))}
        </svg>
      )}
    </div>
  );
}

export default GridOverlay;

